import { Archive, CaretDown } from "@phosphor-icons/react"
import { BaseButton } from "@tokenterminal/core/ui/button/Button"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { MenuButton, MenuItem } from "@tokenterminal/core/ui/menu/MenuButton"
import { BaseMessage } from "@tokenterminal/core/ui/messaging/message/BaseMessage"
import {
  Breadcrumbs,
  Breadcrumb,
  BreadcrumbSeparator,
} from "@tokenterminal/core/ui/navigation/breadcrumbs/Breadcrumbs"

import { type RouterOutputs } from "@tokenterminal/core/utils/trpc"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import { ChainStatusField } from "modules/chain-status"
import { FavoriteProjectIconButton } from "modules/favorites/FavoriteProjectIconButton"
import { useProjectNavigation } from "modules/project/hooks/useProjectNavigation"
import StandardizedListingIcon from "modules/v3/ProjectHero/StandardizedListingIcon"
import Link from "next/link"
import { useEffect, useRef, useMemo } from "react"
import { getProjectPageRoute } from "utils/getProjectPageRoute"
import { type ProductMeta } from "utils/trpc"

type InnerComponentProps = {
  children: React.ReactNode
  projectSlug: string
  projectName: string
  assetName: string
  symbol?: string
  logo?: string
  dataId: string
  tier: "standard" | string
  archivedAt?: string
  archivedReason?: string
  marketSectors: Array<{
    id: string
    name: string
  }>
  activeTab: string
  viewContext:
    | { variant: "project" }
    | { variant: "product"; productName: string; productId: string }
  shouldShowEcosystemTab: boolean
  hasFundraises: boolean
  projectData: RouterOutputs["projects"]["getProject"]
  datasetSlug?: string
}

const InnerComponent = ({
  children,
  projectSlug,
  projectName,
  assetName,
  symbol,
  logo,
  dataId,
  tier,
  archivedAt,
  archivedReason,
  marketSectors,
  activeTab,
  viewContext,
  shouldShowEcosystemTab,
  hasFundraises,
  projectData,
  datasetSlug,
}: InnerComponentProps) => {
  const headerRef = useRef<HTMLDivElement>(null)
  const stickyHeaderRef = useRef<HTMLDivElement>(null)

  // Check if this is a chain project (L1/L2/L3 blockchain)
  const isChainProject = marketSectors.some(
    (sector) =>
      sector.id === "blockchains-l1" ||
      sector.id === "blockchains-l2" ||
      sector.id === "blockchains-l3"
  )

  const availableDatasetItems = useProjectNavigation(
    projectSlug,
    hasFundraises,
    projectData
  )

  // Find the current dataset navigation item to display its label
  const currentDatasetItem = useMemo(() => {
    if (datasetSlug) {
      return availableDatasetItems.find((nav) => nav.value === datasetSlug)
    }
    return null
  }, [datasetSlug, availableDatasetItems])

  useEffect(() => {
    if (!stickyHeaderRef.current) {
      return
    }

    const handleScroll = () => {
      if (!headerRef.current || !stickyHeaderRef.current) return

      const headerBottom = headerRef.current.getBoundingClientRect().bottom
      const shouldShowSticky = headerBottom <= 0

      if (shouldShowSticky) {
        stickyHeaderRef.current.setAttribute("data-scrolled", "true")
        stickyHeaderRef.current.removeAttribute("inert")
      } else {
        stickyHeaderRef.current.setAttribute("data-scrolled", "false")
        stickyHeaderRef.current.setAttribute("inert", "")
      }
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll() // Check initial state

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const tabs = (
    <div className="flex gap-4 px-4 md:px-0 overflow-x-auto -ml-4 -mr-4 md:ml-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      <Link
        className="tt-tab whitespace-nowrap"
        href={getProjectPageRoute({
          projectSlug,
          productId:
            viewContext.variant === "product"
              ? viewContext.productId
              : undefined,
        })}
        data-selected={activeTab === "overview" || undefined}
      >
        Overview
      </Link>
      {shouldShowEcosystemTab && viewContext.variant !== "product" && (
        <Link
          className="tt-tab whitespace-nowrap"
          href={getProjectPageRoute({
            projectSlug,
            ecosystemMetricId: "projects",
          })}
          data-selected={activeTab === "ecosystem" || undefined}
        >
          Ecosystem
        </Link>
      )}
      <Link
        className="tt-tab whitespace-nowrap"
        href={getProjectPageRoute({
          projectSlug,
          productId:
            viewContext.variant === "product"
              ? viewContext.productId
              : undefined,
          metricSlug: "all",
        })}
        data-selected={activeTab === "metrics" || undefined}
      >
        Metrics
      </Link>
      {viewContext.variant !== "product" && (
        <>
          <Link
            className="tt-tab whitespace-nowrap"
            href={`/projects/${projectSlug}/financial-statement`}
            data-selected={activeTab === "financial-statement" || undefined}
          >
            Financial statement
          </Link>
          {availableDatasetItems.length > 0 && (
            <MenuButton
              placement="bottom start"
              trigger={
                <BaseButton
                  className="tt-tab whitespace-nowrap flex items-center gap-1.5 py-0"
                  data-selected={activeTab === "datasets" || undefined}
                >
                  <span>
                    {currentDatasetItem ? currentDatasetItem.label : "Datasets"}
                  </span>
                  <Icon as={CaretDown} className="text-fg-secondary" />
                </BaseButton>
              }
            >
              {availableDatasetItems.map((datasetItem) => (
                <MenuItem
                  key={datasetItem.value}
                  href={`/explorer/projects/${projectSlug}/datasets/${datasetItem.value}`}
                >
                  {datasetItem.label}
                </MenuItem>
              ))}
            </MenuButton>
          )}
        </>
      )}
    </div>
  )

  return (
    <div
      className="w-full relative border-t border-solid border-border-default"
      key={projectSlug}
    >
      {/* Sticky Header */}
      <div
        ref={stickyHeaderRef}
        className={`fixed top-0 left-0 right-0 bg-bg-default border-b border-solid border-border-default transform transition-transform duration-300 z-50 data-[scrolled=true]:translate-y-0 -translate-y-full`}
        inert
      >
        <div className="w-[1540px] px-4 max-w-full mx-auto">
          <div className="flex items-start justify-between py-3">
            <div className="flex items-center gap-2">
              <Avatar
                size="xsmall"
                src={`https://static1.tokenterminal.com/${logo}`}
                className="rounded-full overflow-hidden shrink-0"
              />
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{assetName}</span>
                {symbol && (
                  <span
                    className="text-fg-secondary opacity-80"
                    style={{
                      fontFamily: "var(--font-geist-mono)",
                      fontWeight: 400,
                    }}
                  >
                    {symbol}
                  </span>
                )}
                {tier === "standard" && <StandardizedListingIcon />}
                <FavoriteProjectIconButton dataId={dataId} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header Section */}
      <div ref={headerRef}>
        <div className="w-[1540px] px-4 max-w-full mx-auto">
          <div className="flex flex-col gap-2 mb-4">
            <div className="flex items-center justify-between my-2">
              <Breadcrumbs>
                <Breadcrumb href="/projects">Projects</Breadcrumb>
                <BreadcrumbSeparator />
                <Breadcrumb
                  href={
                    viewContext.variant === "product"
                      ? `/projects/${projectSlug}`
                      : undefined
                  }
                >
                  <div className="flex items-center gap-2">
                    {projectName}
                    <div className="-ml-1.5">
                      <FavoriteProjectIconButton dataId={dataId} />
                    </div>
                  </div>
                </Breadcrumb>
                {viewContext.variant === "product" && (
                  <>
                    <BreadcrumbSeparator />
                    <Breadcrumb>{viewContext.productName}</Breadcrumb>
                  </>
                )}
              </Breadcrumbs>
              {isChainProject && <ChainStatusField chainApiId={projectSlug} />}
            </div>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between mt-5 gap-4">
              <div className="flex gap-3">
                <Avatar
                  size="small"
                  src={`https://static1.tokenterminal.com/${logo}`}
                  className="rounded-full overflow-hidden shrink-0 mt-0.5"
                />
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Heading level={1} className="flex items-center gap-2">
                      {viewContext.variant === "product"
                        ? viewContext.productName
                        : projectName}
                      {symbol && (
                        <span
                          className="text-fg-secondary"
                          style={{
                            fontFamily: "var(--font-geist-mono)",
                            fontWeight: 400,
                            fontSize: "0.9em",
                          }}
                        >
                          {symbol}
                        </span>
                      )}
                    </Heading>
                    {tier === "standard" && (
                      <StandardizedListingIcon iconWidth={16} />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {marketSectors.map((sector) => (
                      <Link
                        key={sector.name}
                        href={`/markets/${sector.id}`}
                        className="text-sm text-fg-secondary hover:text-fg-primary hover:underline"
                      >
                        {sector.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {archivedAt && (
        <div className="w-[1540px] my-6 px-4 max-w-full mx-auto">
          <BaseMessage
            title="Archived"
            children={archivedReason}
            icon={Archive}
          />
        </div>
      )}
      {/* Navigation Tabs */}
      <div className="w-[1540px] px-4 max-w-full mx-auto">{tabs}</div>
      <div className="w-full h-px bg-border-default" />
      <div className="w-[1540px] px-4 max-w-full mx-auto mt-8">{children}</div>
    </div>
  )
}

type ProjectDetailPageProps = {
  children: React.ReactNode
  slug: string
  projectData: RouterOutputs["projects"]["getProject"]
  activeTab: string
  viewContext?:
    | { variant: "project" }
    | {
        variant: "product"
        productData: ProductMeta
      }
  datasetSlug?: string
}

function parseInnerComponentProps({
  children,
  slug,
  projectData,
  activeTab,
  viewContext = { variant: "project" },
  datasetSlug,
}: ProjectDetailPageProps): InnerComponentProps {
  const baseProps = {
    children,
    projectSlug: slug,
    projectName: projectData.name,
    activeTab,
    dataId: projectData.data_id,
    tier: projectData.tier,
    archivedAt: projectData.archived_at,
    archivedReason: projectData.archived_reason,
    marketSectors: projectData.market_sectors,
    shouldShowEcosystemTab: projectData.is_ecosystem,
    hasFundraises: Boolean(projectData.has_fundraises),
    projectData,
    datasetSlug,
  }

  if (viewContext.variant === "product") {
    const { productData } = viewContext
    return {
      ...baseProps,
      assetName: productData.product_name,
      symbol: productData.symbol ?? projectData.symbol,
      logo: productData.logo ?? projectData.logo,
      viewContext: {
        variant: "product",
        productName: productData.product_name,
        productId: productData.product_id,
      },
    }
  }

  return {
    ...baseProps,
    assetName: projectData.name,
    symbol: projectData.symbol,
    logo: projectData.logo,
    viewContext: { variant: "project" },
  }
}

export function ProjectDetailPage(props: ProjectDetailPageProps) {
  return (
    <ClientOnlySuspense fallback={null}>
      <InnerComponent {...parseInnerComponentProps(props)} />
    </ClientOnlySuspense>
  )
}
